import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiCall } from 'use-api-call';
import { LocalStorageKeys } from '@constants/enums';
import { UploadStaStakeHolders } from '@efex-services/client/types';
import { useFormContext } from 'react-hook-form';
import { captureException } from '@sentry/react';
import { UseLifeProofProps, UseLifeProofReturn } from './useLifeProof.types';
import aiPriseSvc from '../services';

const useLifeProof = ({
  getStakeHolder,
  handleStakeholderRequest,
  getAllCurrentDocumentsIds,
}: UseLifeProofProps): UseLifeProofReturn => {
  const [isOpenWebFrame, setIsOpenWebFrame] = useState(false);
  const [isOpenObtainLink, setIsOpenObtainLink] = useState(false);

  const accessToken = localStorage.getItem(LocalStorageKeys.TOKEN) || '';

  const { watch } = useFormContext();

  const name = watch('name');
  const lastName = watch('lastName');

  const stakeholderId = useRef<number | null>(null);
  const modalToOpen = useRef<'webFrame' | 'obtainLink' | null>(null);

  const {
    invoke: getUserVerificationId,
    loading: loadingGetUserVerificationId,
    data: userVerificationId,
  } = useApiCall(() =>
    aiPriseSvc.getUserVerificationId({
      userToken: accessToken,
      userId: stakeholderId.current ?? 0,
      userData: {
        first_name: name,
        last_name: lastName,
      },
    })
  );

  const submitStakeholder = useCallback(
    async (data: UploadStaStakeHolders) => {
      try {
        const documentIds = getAllCurrentDocumentsIds();
        const response = await handleStakeholderRequest(data, documentIds);
        stakeholderId.current = response.id ?? null;
      } catch (error) {
        captureException(error);
      }
    },
    [handleStakeholderRequest, getAllCurrentDocumentsIds]
  );

  const handleCloseWebFrame = (): void => {
    setIsOpenWebFrame(false);
    getStakeHolder();
  };

  const handleOpenWebFrame = async (
    data: UploadStaStakeHolders
  ): Promise<void> => {
    await submitStakeholder(data);
    getUserVerificationId();
    modalToOpen.current = 'webFrame';
  };

  const handleCloseObtainLink = (): void => {
    setIsOpenObtainLink(false);
    getStakeHolder();
  };

  const handleGetUserVerificationId = async (
    data: UploadStaStakeHolders
  ): Promise<void> => {
    await submitStakeholder(data);
    getUserVerificationId();
    modalToOpen.current = 'obtainLink';
  };

  useEffect(() => {
    if (userVerificationId?.data?.verification_url) {
      if (modalToOpen.current === 'webFrame') {
        setIsOpenWebFrame(true);
      } else if (modalToOpen.current === 'obtainLink') {
        setIsOpenObtainLink(true);
      }
    }
  }, [userVerificationId]);

  return {
    isOpenWebFrame,
    isOpenObtainLink,
    loadingGetUserVerificationId,
    userVerificationId: userVerificationId?.data?.verification_url || '',
    handleCloseWebFrame,
    handleOpenWebFrame,
    handleCloseObtainLink,
    handleGetUserVerificationId,
    sessionId:
      userVerificationId?.data?.verification_url?.split('=')[1] || null,
  };
};

export default useLifeProof;
